import {
  DevicePairingResponse,
  EntitiesIdentityDocumentKeyIdDocType,
  IdentificationHandlersEnrollmentResponse,
  IdentificationHandlersIdentificationCaseResponse,
  IdentificationHandlersIdentificationDocumentChallengeStartResponse,
  IdentificationHandlersIdentificationIdentityResponse,
  IdentificationHandlersIdentificationPersonalDetailsDocEnriched,
  IdentificationHandlersIdentificationStatus,
  IdentificationHandlersProcessLivelinessImagesRequest,
  IdentificationHandlersWebEnrollmentResponse,
  IdentificationHandlersWebReEnrollmentResponse,
  WeakLoginResponse,
  WebAuthnBeginRegistrationResponse,
} from '@deepcloud/api-deep-id'
import { ValueOf } from '@deepcloud/deep-ui-utils'

export const STEP_ID = {
  // General Steps
  TODO: 'todo',
  LOGIN: 'login',
  ERROR: 'error',
  WAITING: 'waiting',

  // Ident Steps
  INTRO_DEEP_ID: 'intro-deep-id',
  SCAN_QR_EXPLANATION: 'scan-qr-explanation',
  SCAN_QR: 'scan-qr',
  ENTER_VOUCHER: 'enter-voucher',
  DEVICE_NOT_SUPPORTED: 'device-not-supported',
  COUNTRY_SELECTION: 'country-selection',
  COUNTRY_NOT_SUPPORTED: 'country-not-supported',
  PROCESS_EXPLANATION_1: 'process-explanation-1',
  HANDOVER: 'handover',
  HANDOVER_CONTINUE: 'handover-continue',
  HANDOVER_RETURN: 'handover-return',
  DOCUMENT_SELECTION: 'document-selection',
  DOCUMENT_RESIDENCE_PERMIT_INFO: 'document-residence-permit-info',
  DOCUMENT_PICTURE_ERROR: 'document-picture-error',
  DOCUMENT_CHALLENGE_ERROR: 'document-challenge-error',
  PASSPORT_EXPLANATION_MRZ: 'passport-explanation-mrz',
  PASSPORT_PICTURE_MRZ: 'passport-picture-mrz',
  PASSPORT_EXPLANATION_SIGNATURE: 'passport-explanation-signature',
  PASSPORT_PICTURE_SIGNATURE: 'passport-picture-signature',
  PASSPORT_EXPLANATION_MRZ_FEATURE_CHALLENGE:
    'passport-explanation-mrz-feature-challenge',
  PASSPORT_MRZ_FEATURE_CHALLENGE: 'passport-mrz-feature-challenge',
  PASSPORT_EXPLANATION_MRZ_PRESENTATION_CHALLENGE:
    'passport-explanation-mrz-presentation-challenge',
  PASSPORT_MRZ_PRESENTATION_CHALLENGE: 'passport-mrz-presentation-challenge',
  IDENTITY_CARD_EXPLANATION_MRZ: 'identity-card-explanation-mrz',
  IDENTITY_CARD_PICTURE_MRZ: 'identity-card-picture-mrz',
  IDENTITY_CARD_EXPLANATION_FRONT: 'identity-card-explanation-front',
  IDENTITY_CARD_PICTURE_FRONT: 'identity-card-picture-front',
  IDENTITY_CARD_EXPLANATION_MRZ_FEATURE_CHALLENGE:
    'identity-card-explanation-mrz-feature-challenge',
  IDENTITY_CARD_MRZ_FEATURE_CHALLENGE: 'identity-card-mrz-feature-challenge',
  IDENTITY_CARD_EXPLANATION_FRONT_FEATURE_CHALLENGE:
    'identity-card-explanation-front-feature-challenge',
  IDENTITY_CARD_FRONT_FEATURE_CHALLENGE:
    'identity-card-front-feature-challenge',
  IDENTITY_CARD_EXPLANATION_MRZ_PRESENTATION_CHALLENGE:
    'identity-card-explanation-mrz-presentation-challenge',
  IDENTITY_CARD_MRZ_PRESENTATION_CHALLENGE:
    'identity-card-mrz-presentation-challenge',
  IDENTITY_CARD_EXPLANATION_FRONT_PRESENTATION_CHALLENGE:
    'identity-card-explanation-front-presentation-challenge',
  IDENTITY_CARD_FRONT_PRESENTATION_CHALLENGE:
    'identity-card-front-presentation-challenge',
  PROCESS_EXPLANATION_2: 'process-explanation-2',
  PORTRAIT_EXPLANATION_PICTURE: 'portrait-explanation-picture',
  PORTRAIT_PICTURE: 'portrait-picture',
  PORTRAIT_PICTURE_ERROR: 'portrait-picture-error',
  PORTRAIT_EXPLANATION_VIDEO: 'portrait-explanation-video',
  PORTRAIT_VIDEO_LIVENESS: 'portrait-video-liveness',
  PROCESS_EXPLANATION_3: 'process-explanation-3',
  PERSONAL_DETAILS: 'personal-details',
  DISPLAY_NAME: 'display-name',
  CHECK_NAMES: 'check-names',
  EXISTING_IDENTITY: 'existing-identity',
  DOMICILE_ADDRESS: 'domicile-address',
  PHONE_NUMBER: 'phone-number',
  PHONE_NUMBER_VERIFY: 'phone-number-verify',
  EMAIL_ADDRESS: 'email-address',
  EMAIL_ADDRESS_VERIFY: 'email-address-verify',
  RECOVERY_CODE: 'recovery-code',
  RECOVERY_CODE_CHALLENGE: 'recovery-code-challenge',
  RECOVERY_CODE_DECLINED: 'recovery-code-declined',
  CASE_CONDITIONS: 'case-conditions',
  IDENTIFICATION_PENDING: 'identification-pending',
  USER_ENROLLMENT: 'user-enrollment',
  USER_REGISTRATION: 'user-registration',

  // Re-Ident Steps
  RE_IDENT_STEP_1: 're-ident-1',
  DATE_OF_BIRTH: 'date-of-birth',
  RE_IDENT_STEP_2: 're-ident-2',
  RE_IDENT_STEP_3: 're-ident-3',
  RE_IDENT_EXTENDED: 're-ident-extended',
  RE_IDENT_CERTIFICATION: 're-ident-certification',

  CERTIFICATION_DECLINED_SOFT: 'certification-retry',
  CERTIFICATION_DECLINED_HARD: 'certification-support',
  CERTIFICATION_NO_MATCH: 'certification-no-match',
} as const

export type StepId = ValueOf<typeof STEP_ID>

export const BACK_BEHAVIOUR = {
  BACK: 'back',
  CANCEL: 'cancel',
} as const

type BackBehaviour = ValueOf<typeof BACK_BEHAVIOUR>

export type FlowData = {
  // short live token for device handover
  token?: string

  voucherCode?: string
  deviceId?: string // the deviceId generated when an app is installed on native apps
  deviceToken?: string // the deviceToken stored in the keychain of the user
  deviceInfo?: string
  transferCode?: string
  isTransferOriginDevice?: boolean
  isTransferTargetDevice?: boolean
  isDeviceSupported?: boolean
  deviceCameraId?: string | null // the camera id with torch support (null = default device)
  hasChangedDisplayName?: boolean
  identification?: IdentificationHandlersIdentificationIdentityResponse
  identificationCase?: IdentificationHandlersIdentificationCaseResponse
  documentType?: EntitiesIdentityDocumentKeyIdDocType
  caseStatus?: IdentificationHandlersIdentificationStatus
  livenessImages?: IdentificationHandlersProcessLivelinessImagesRequest
  mrzFeatureChallenge?: IdentificationHandlersIdentificationDocumentChallengeStartResponse
  frontFeatureChallenge?: IdentificationHandlersIdentificationDocumentChallengeStartResponse
  personalDetails?: IdentificationHandlersIdentificationPersonalDetailsDocEnriched
  enrollment?: IdentificationHandlersEnrollmentResponse
  webEnrollment?: IdentificationHandlersWebEnrollmentResponse
  webReEnrollment?: IdentificationHandlersWebReEnrollmentResponse
  webAuthnBeginRegistration?: WebAuthnBeginRegistrationResponse
  weakLogin?: WeakLoginResponse
  devicePairing?: DevicePairingResponse
  extendedReIdentStarted?: boolean
}

export type StepEnterResolver<
  TDataEnter extends StepData = StepData,
  TDataReturn extends StepData = TDataEnter,
> = (stepData?: TDataEnter) => Promise<TDataReturn | void>

type CanVisitResolver = (stepData: StepData) => boolean

export type StepNextResolver<TDataNext = unknown> =
  StepId | ((stepData: TDataNext) => Promise<StepNavigation>)

export type StepNavigation<TData = StepData> = {
  stepId?: StepId
  data?: TData
  replace?: boolean
}

export type StepData = Record<string, unknown>

export interface IdentStep<
  TDataEnter extends StepData = StepData,
  TDataNext = unknown,
> {
  id: StepId
  component: string
  props?: TDataEnter
  canVisit?: CanVisitResolver
  onEnter?: StepEnterResolver<TDataEnter>
  onNext?: StepNextResolver<TDataNext>
  backBehaviour?: BackBehaviour
}

export interface StepHandler<
  TDataEnter extends StepData = StepData,
  TDataNext = unknown,
> {
  canVisit?: CanVisitResolver
  onEnter?: StepEnterResolver<StepData, TDataEnter>
  onNext?: StepNextResolver<TDataNext>
}

export type StepHandlers<
  TDataEnter extends StepData = StepData,
  TDataNext = unknown,
> = Partial<Record<string, StepHandler<TDataEnter, TDataNext>>>

export interface PersistedState {
  currentId: StepId
  data: StepData
  flowData: FlowData
}
